import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, BookOpen, Upload, FileText, Trash2, Edit2, Users, Link as LinkIcon, Clock, RefreshCw, Webhook, BarChart3, Play, Pause, Eye, Save } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function KnowledgeBase() {
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [selectedKB, setSelectedKB] = useState(null);
  const [actors, setActors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("items");
  
  // KB Management
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [manualItemDialogOpen, setManualItemDialogOpen] = useState(false);
  const [websiteDialogOpen, setWebsiteDialogOpen] = useState(false);
  const [editingKB, setEditingKB] = useState(null);
  
  // Website Monitoring
  const [monitors, setMonitors] = useState([]);
  const [monitorDialogOpen, setMonitorDialogOpen] = useState(false);
  const [monitorConfig, setMonitorConfig] = useState({
    url: "",
    update_frequency: 24
  });
  
  // Webhooks
  const [webhooks, setWebhooks] = useState([]);
  const [webhookDialogOpen, setWebhookDialogOpen] = useState(false);
  const [webhookConfig, setWebhookConfig] = useState({
    name: "",
    webhook_url: "",
    auth_type: "none",
    auth_token: "",
    sync_frequency: 24,
    primary_key: "userId",
    entity_rules: [],
    custom_tags: []
  });
  const [webhookStep, setWebhookStep] = useState(1); // 1: Basic, 2: Entity Config
  const [analyzingAPI, setAnalyzingAPI] = useState(false);
  const [apiAnalysis, setApiAnalysis] = useState(null);
  const [customTagInput, setCustomTagInput] = useState("");
  const [webhookStats, setWebhookStats] = useState({});
  const [loadingStats, setLoadingStats] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [syncProgress, setSyncProgress] = useState({}); // {webhookId: {status, progress, etc}}
  const [pausedSyncs, setPausedSyncs] = useState(new Set());
  const [pollingInterval, setPollingInterval] = useState(null);
  
  // Analytics
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(7);
  
  // Full document viewer/editor
  const [fullContentDialogOpen, setFullContentDialogOpen] = useState(false);
  const [fullDocumentContent, setFullDocumentContent] = useState("");
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [editingContent, setEditingContent] = useState(false);
  const [loadingFullContent, setLoadingFullContent] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    associated_actors: []
  });
  
  const [uploadFile, setUploadFile] = useState(null);
  const [correctionModel, setCorrectionModel] = useState("google/gemini-2.0-flash-exp:free");
  const [openrouterModels, setOpenrouterModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [manualItem, setManualItem] = useState({
    content: "",
    source_name: "",
    tags: []  // Add tags field
  });
  const [currentTag, setCurrentTag] = useState("");  // For tag input
  
  // Tags editing state
  const [editingTags, setEditingTags] = useState(null);  // {itemId, tags: []}
  const [editTagInput, setEditTagInput] = useState("");

  useEffect(() => {
    fetchKnowledgeBases();
    fetchActors();
    fetchOpenRouterModels();
  }, []);

  // Polling for active syncs
  useEffect(() => {
    // Check if any sync is active
    const hasActiveSyncs = Object.values(syncProgress).some(p => p.status === 'syncing');
    
    if (hasActiveSyncs && selectedKB) {
      // Start polling every 5 seconds
      const interval = setInterval(() => {
        fetchWebhooks(selectedKB.id);
        fetchKBDetails(selectedKB.id);
      }, 5000);
      
      setPollingInterval(interval);
      
      return () => {
        clearInterval(interval);
      };
    } else if (pollingInterval) {
      // Stop polling when no active syncs
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  }, [syncProgress, selectedKB]);
  
  const fetchOpenRouterModels = async () => {
    setLoadingModels(true);
    try {
      const response = await axios.get("https://openrouter.ai/api/v1/models");
      setOpenrouterModels(response.data.data || []);
    } catch (error) {
      console.error("Failed to fetch models:", error);
      toast.error("Failed to load LLM models");
    } finally {
      setLoadingModels(false);
    }
  };

  useEffect(() => {
    if (selectedKB) {
      if (activeTab === "monitors") {
        fetchMonitors(selectedKB.id);
      } else if (activeTab === "webhooks") {
        fetchWebhooks(selectedKB.id);
      } else if (activeTab === "analytics") {
        fetchAnalytics(selectedKB.id, analyticsDays);
      }
    }
  }, [selectedKB, activeTab, analyticsDays]);

  const fetchKnowledgeBases = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/knowledge-bases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKnowledgeBases(response.data.knowledge_bases || []);
    } catch (error) {
      console.error("Failed to fetch knowledge bases", error);
      toast.error("Failed to load knowledge bases");
    } finally {
      setLoading(false);
    }
  };

  const fetchActors = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/ai/actors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActors(response.data || []);
    } catch (error) {
      console.error("Failed to fetch actors", error);
    }
  };

  const fetchKBDetails = async (kbId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/knowledge-bases/${kbId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedKB(response.data);
    } catch (error) {
      console.error("Failed to fetch KB details", error);
      toast.error("Failed to load knowledge base details");
    }
  };

  const fetchMonitors = async (kbId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/knowledge-bases/${kbId}/monitors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonitors(response.data.monitors || []);
    } catch (error) {
      console.error("Failed to fetch monitors", error);
    }
  };

  const fetchWebhooks = async (kbId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/knowledge-bases/${kbId}/webhooks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const webhooksData = response.data.webhooks || response.data;
      setWebhooks(webhooksData);
      
      // Update syncProgress from backend sync_status
      const newSyncProgress = {};
      webhooksData.forEach(webhook => {
        if (webhook.sync_status && webhook.sync_status.is_syncing) {
          newSyncProgress[webhook.id] = {
            status: 'syncing',
            progress_percentage: webhook.sync_status.progress_percentage || 0,
            clients_processed: webhook.sync_status.clients_processed || 0,
            items_added: webhook.sync_status.items_added || 0,
            items_skipped: webhook.sync_status.items_skipped || 0,
            total_in_response: webhook.sync_status.total_items || 0,
            current_batch: webhook.sync_status.current_batch || 0,
            total_batches: webhook.sync_status.total_batches || 0,
            elapsed_time: webhook.sync_status.elapsed_time || '0s',
            eta: webhook.sync_status.eta || 'Calculating...',
            next_offset: webhook.sync_status.current_offset || 0,
            has_more: webhook.sync_status.is_syncing,
            start_time: webhook.sync_status.start_time ? new Date(webhook.sync_status.start_time).getTime() : Date.now()
          };
        }
      });
      
      setSyncProgress(prev => ({...prev, ...newSyncProgress}));
      
      // Auto-fetch stats for each webhook
      webhooksData.forEach(webhook => {
        fetchWebhookStats(webhook.id, true); // true = silent mode (no alert)
      });
    } catch (error) {
      console.error("Error fetching webhooks:", error);
    }
  };

  const fetchAnalytics = async (kbId, days) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/knowledge-bases/${kbId}/analytics?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error("Failed to fetch analytics", error);
    }
  };

  const handleCreate = async () => {
    if (!formData.name) {
      toast.error("Please enter a name");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/knowledge-bases`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Knowledge base created successfully");
      setDialogOpen(false);
      setFormData({ name: "", description: "", associated_actors: [] });
      fetchKnowledgeBases();
    } catch (error) {
      toast.error("Failed to create knowledge base");
    }
  };

  const handleUpdate = async () => {
    if (!editingKB) return;

    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/knowledge-bases/${editingKB.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Knowledge base updated successfully");
      setDialogOpen(false);
      setEditingKB(null);
      setFormData({ name: "", description: "", associated_actors: [] });
      fetchKnowledgeBases();
      if (selectedKB && selectedKB.id === editingKB.id) {
        fetchKBDetails(editingKB.id);
      }
    } catch (error) {
      toast.error("Failed to update knowledge base");
    }
  };

  const handleDelete = async (kbId) => {
    if (!window.confirm("Are you sure you want to delete this knowledge base? All items will be deleted.")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/knowledge-bases/${kbId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Knowledge base deleted successfully");
      fetchKnowledgeBases();
      if (selectedKB && selectedKB.id === kbId) {
        setSelectedKB(null);
      }
    } catch (error) {
      toast.error("Failed to delete knowledge base");
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadFile || !selectedKB) {
      toast.error("Please select a file");
      return;
    }

    try {
      setUploadingDocument(true);
      setUploadProgress(0);
      setUploadStatus("Uploading document...");
      
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("correction_model", correctionModel);

      // Simulate progress for file upload
      setUploadProgress(20);
      setUploadStatus("Extracting text from PDF...");
      
      // Small delay to show progress
      await new Promise(resolve => setTimeout(resolve, 500));
      setUploadProgress(40);
      setUploadStatus("Applying AI correction (this may take 10-30 seconds)...");

      const response = await axios.post(
        `${API}/admin/knowledge-bases/${selectedKB.id}/upload-document`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 40) / progressEvent.total);
            setUploadProgress(Math.min(percentCompleted + 40, 80));
          }
        }
      );

      setUploadProgress(90);
      setUploadStatus("Creating chunks and embeddings...");
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setUploadProgress(100);
      setUploadStatus("Complete!");
      
      toast.success(response.data.message);
      
      // Reset and close after a brief delay
      setTimeout(() => {
        setUploadDialogOpen(false);
        setUploadFile(null);
        setUploadingDocument(false);
        setUploadProgress(0);
        setUploadStatus("");
        fetchKBDetails(selectedKB.id);
      }, 1000);
      
    } catch (error) {
      setUploadingDocument(false);
      setUploadProgress(0);
      setUploadStatus("");
      toast.error(error.response?.data?.detail || "Failed to upload document");
    }
  };

  const handleAddManualItem = async () => {
    if (!manualItem.content || !selectedKB) {
      toast.error("Please enter content");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/knowledge-bases/${selectedKB.id}/items`,
        {
          content: manualItem.content,
          source_type: "manual",
          source_name: manualItem.source_name || "Manual Entry",
          metadata: {},
          tags: manualItem.tags || []  // Include tags
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Item added successfully");
      setManualItemDialogOpen(false);
      setManualItem({ content: "", source_name: "", tags: [] });  // Reset tags
      setCurrentTag("");  // Reset current tag input
      fetchKBDetails(selectedKB.id);
    } catch (error) {
      toast.error("Failed to add item");
    }
  };
  
  const handleUpdateItemTags = async (itemId, newTags) => {
    try {
      const token = localStorage.getItem("token");
      console.log("Updating tags for item:", itemId, "with tags:", newTags);
      
      const response = await axios.patch(
        `${API}/admin/knowledge-bases/${selectedKB.id}/items/${itemId}/tags`,
        newTags,
        { 
          headers: { 
            Authorization: `Bearer ${token}`, 
            'Content-Type': 'application/json' 
          } 
        }
      );
      
      console.log("Tags update response:", response.data);
      toast.success("Tags updated successfully");
      setEditingTags(null);
      setEditTagInput("");
      
      // Refresh KB details to show updated tags
      await fetchKBDetails(selectedKB.id);
      console.log("KB details refreshed");
    } catch (error) {
      console.error("Failed to update tags:", error.response || error);
      toast.error("Failed to update tags: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddWebsite = async () => {
    if (!websiteUrl || !selectedKB) {
      toast.error("Please enter a website URL");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/knowledge-bases/${selectedKB.id}/add-website?url=${encodeURIComponent(websiteUrl)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      setWebsiteDialogOpen(false);
      setWebsiteUrl("");
      fetchKBDetails(selectedKB.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to scrape website");
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm("Are you sure you want to delete this item?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/knowledge-bases/${selectedKB.id}/items/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Item deleted successfully");
      fetchKBDetails(selectedKB.id);
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  const handleAddMonitor = async () => {
    if (!monitorConfig.url || !selectedKB) {
      toast.error("Please enter a URL");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/knowledge-bases/${selectedKB.id}/monitor-website`,
        monitorConfig,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Website monitoring configured");
      setMonitorDialogOpen(false);
      setMonitorConfig({ url: "", update_frequency: 24 });
      fetchMonitors(selectedKB.id);
    } catch (error) {
      toast.error("Failed to configure monitoring");
    }
  };

  const handleTriggerMonitorUpdate = async (monitorId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/website-monitors/${monitorId}/update`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      fetchMonitors(selectedKB.id);
      fetchKBDetails(selectedKB.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update website");
    }
  };
  
  const handleViewFullDocument = async (sourceName) => {
    setLoadingFullContent(true);
    setFullContentDialogOpen(true);
    setSelectedDocument(sourceName);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/admin/knowledge-bases/${selectedKB.id}/document/${encodeURIComponent(sourceName)}/full-content`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (!response.data.full_content || response.data.full_content.trim() === "") {
        toast.error("No content found for this document");
        setFullDocumentContent("No content available.");
      } else {
        setFullDocumentContent(response.data.full_content);
      }
      setEditingContent(false);
    } catch (error) {
      console.error("Failed to load document:", error);
      toast.error(error.response?.data?.detail || "Failed to load full document content");
      setFullDocumentContent("Error loading content.");
    } finally {
      setLoadingFullContent(false);
    }
  };
  
  const handleUpdateFullDocument = async () => {
    if (!fullDocumentContent.trim()) {
      toast.error("Content cannot be empty");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/admin/knowledge-bases/${selectedKB.id}/document/${encodeURIComponent(selectedDocument)}/full-content`,
        fullDocumentContent,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'text/plain'
          }
        }
      );
      toast.success("Document updated successfully");
      setFullContentDialogOpen(false);
      setEditingContent(false);
      fetchKBDetails(selectedKB.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update document");
    }
  };

  const handleDeleteMonitor = async (monitorId) => {
    if (!window.confirm("Are you sure you want to delete this monitor?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/website-monitors/${monitorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Monitor deleted");
      fetchMonitors(selectedKB.id);
    } catch (error) {
      toast.error("Failed to delete monitor");
    }
  };

  const handleAnalyzeAPI = async () => {
    if (!webhookConfig.webhook_url || !selectedKB) {
      toast.error("Please enter webhook URL");
      return;
    }

    setAnalyzingAPI(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/knowledge-bases/${selectedKB.id}/webhooks/analyze`,
        {
          url: webhookConfig.webhook_url,
          auth_type: webhookConfig.auth_type,
          auth_token: webhookConfig.auth_token
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setApiAnalysis(response.data);
      
      // Auto-configure based on analysis
      const detectedRules = response.data.detected_entities.map(entity => ({
        name: entity.name,
        path: entity.path,
        tags: [entity.name.toLowerCase().replace(/ /g, '-')],
        enabled: true
      }));
      
      setWebhookConfig(prev => ({
        ...prev,
        primary_key: response.data.suggested_primary_key || "userId",
        entity_rules: detectedRules
      }));
      
      setWebhookStep(2); // Move to entity configuration step
      toast.success(`API analyzed! Found ${response.data.detected_entities.length} entities`);
    } catch (error) {
      toast.error("Failed to analyze API: " + (error.response?.data?.detail || error.message));
    } finally {
      setAnalyzingAPI(false);
    }
  };

  const handleAddWebhook = async () => {
    if (!webhookConfig.name || !webhookConfig.webhook_url || !selectedKB) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/knowledge-bases/${selectedKB.id}/webhooks`,
        webhookConfig,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Webhook configured successfully");
      setWebhookDialogOpen(false);
      setWebhookConfig({
        name: "",
        webhook_url: "",
        auth_type: "none",
        auth_token: "",
        sync_frequency: 24,
        primary_key: "userId",
        entity_rules: [],
        custom_tags: []
      });
      setWebhookStep(1);
      setApiAnalysis(null);
      fetchWebhooks(selectedKB.id);
    } catch (error) {
      toast.error("Failed to configure webhook");
    }
  };

  const handleTriggerWebhookSync = async (webhookId, offset = 0, startTime = null) => {
    // Check if paused
    if (pausedSyncs.has(webhookId)) {
      return;
    }
    
    try {
      // Initialize start time
      if (!startTime) {
        startTime = Date.now();
      }
      
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/webhooks/${webhookId}/sync`,
        { offset },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = response.data;
      
      // Calculate elapsed time
      const elapsedMs = Date.now() - startTime;
      const elapsedMin = Math.floor(elapsedMs / 60000);
      const elapsedSec = Math.floor((elapsedMs % 60000) / 1000);
      const elapsedTime = elapsedMin > 0 ? `${elapsedMin}m ${elapsedSec}s` : `${elapsedSec}s`;
      
      // Calculate ETA
      let eta = "Calculating...";
      if (data.progress_percentage > 0 && data.progress_percentage < 100) {
        const remainingPercentage = 100 - data.progress_percentage;
        const estimatedTotalMs = (elapsedMs / data.progress_percentage) * 100;
        const remainingMs = estimatedTotalMs - elapsedMs;
        const remainingMin = Math.floor(remainingMs / 60000);
        const remainingHr = Math.floor(remainingMin / 60);
        
        if (remainingHr > 0) {
          eta = `${remainingHr}h ${remainingMin % 60}m`;
        } else {
          eta = `${remainingMin}m`;
        }
      }
      
      // Update progress state
      setSyncProgress(prev => ({
        ...prev,
        [webhookId]: {
          status: data.has_more ? 'syncing' : 'completed',
          progress_percentage: data.progress_percentage,
          clients_processed: data.clients_processed,
          items_added: data.items_added,
          items_skipped: data.items_skipped,
          total_in_response: data.total_in_response,
          current_batch: Math.floor(offset / 1000) + 1,
          total_batches: Math.ceil(data.total_in_response / 1000),
          elapsed_time: elapsedTime,
          eta: eta,
          offset: data.offset,
          next_offset: data.next_offset,
          has_more: data.has_more,
          start_time: startTime
        }
      }));
      
      // Show toast for first batch only
      if (offset === 0) {
        toast.success(`🔄 Sync started! Processing ${data.total_in_response.toLocaleString()} profiles...`);
      }
      
      // Automatically continue if there's more data and not paused
      if (data.has_more && !pausedSyncs.has(webhookId)) {
        // Refresh UI
        await fetchKBDetails(selectedKB.id);
        
        // Continue with next batch immediately
        setTimeout(() => {
          handleTriggerWebhookSync(webhookId, data.next_offset, startTime);
        }, 500);
      } else if (!data.has_more) {
        // Final completion
        setSyncProgress(prev => ({
          ...prev,
          [webhookId]: {
            ...prev[webhookId],
            status: 'completed'
          }
        }));
        toast.success(`🎉 Sync complete! ${data.clients_processed.toLocaleString()} clients, ${data.items_added.toLocaleString()} items created.`);
        fetchWebhooks(selectedKB.id);
        fetchKBDetails(selectedKB.id);
      }
    } catch (error) {
      setSyncProgress(prev => ({
        ...prev,
        [webhookId]: {
          ...(prev[webhookId] || {}),
          status: 'failed',
          error: error.response?.data?.detail || error.message
        }
      }));
      toast.error(error.response?.data?.detail || "Failed to sync webhook");
      fetchKBDetails(selectedKB.id);
    }
  };

  const handlePauseSync = (webhookId) => {
    setPausedSyncs(prev => new Set([...prev, webhookId]));
    setSyncProgress(prev => ({
      ...prev,
      [webhookId]: {
        ...(prev[webhookId] || {}),
        status: 'paused'
      }
    }));
    toast.info("Sync paused. Click Resume to continue.");
  };

  const handleResumeSync = (webhookId) => {
    const progress = syncProgress[webhookId];
    if (!progress) return;
    
    setPausedSyncs(prev => {
      const newSet = new Set(prev);
      newSet.delete(webhookId);
      return newSet;
    });
    
    setSyncProgress(prev => ({
      ...prev,
      [webhookId]: {
        ...prev[webhookId],
        status: 'syncing'
      }
    }));
    
    toast.info("Resuming sync...");
    handleTriggerWebhookSync(webhookId, progress.next_offset, progress.start_time);
  };

  const handleCancelSync = (webhookId) => {
    if (!window.confirm("Are you sure you want to cancel this sync? Progress will be lost.")) {
      return;
    }
    
    setPausedSyncs(prev => {
      const newSet = new Set(prev);
      newSet.delete(webhookId);
      return newSet;
    });
    
    setSyncProgress(prev => {
      const newState = {...prev};
      delete newState[webhookId];
      return newState;
    });
    
    toast.success("Sync cancelled");
    fetchKBDetails(selectedKB.id);
  };

  const handleDeleteWebhook = async (webhookId) => {
    if (!window.confirm("Are you sure you want to delete this webhook?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/webhooks/${webhookId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Webhook deleted");
      fetchWebhooks(selectedKB.id);
    } catch (error) {
      toast.error("Failed to delete webhook");
    }
  };

  const fetchWebhookStats = async (webhookId, silent = false) => {
    setLoadingStats(prev => ({...prev, [webhookId]: true}));
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/admin/webhooks/${webhookId}/stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWebhookStats(prev => ({...prev, [webhookId]: response.data}));
      
      if (!silent) {
        const data = response.data;
        const message = `📊 Webhook Statistics\n\n` +
          `Total Items: ${data.total_items}\n` +
          `Unique Clients: ${data.unique_clients}\n\n` +
          `Entity Breakdown:\n` +
          Object.entries(data.entity_breakdown).map(([entity, count]) => 
            `  • ${entity}: ${count}`
          ).join('\n') +
          `\n\nSample Clients:\n${data.client_names.slice(0, 10).join(', ')}`;
        
        alert(message);
      }
    } catch (error) {
      if (!silent) {
        toast.error("Failed to fetch webhook statistics");
      }
    } finally {
      setLoadingStats(prev => ({...prev, [webhookId]: false}));
    }
  };

  const openEditDialog = (kb) => {
    setEditingKB(kb);
    setFormData({
      name: kb.name,
      description: kb.description || "",
      associated_actors: kb.associated_actors || []
    });
    setDialogOpen(true);
  };

  const toggleActorAssociation = (actorId) => {
    setFormData(prev => ({
      ...prev,
      associated_actors: prev.associated_actors.includes(actorId)
        ? prev.associated_actors.filter(id => id !== actorId)
        : [...prev.associated_actors, actorId]
    }));
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Knowledge Base</h1>
          <p className="text-muted-foreground">Manage knowledge bases with advanced monitoring and automation</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => {
              setEditingKB(null);
              setFormData({ name: "", description: "", associated_actors: [] });
            }}>
              <Plus className="w-4 h-4 mr-2" />
              Create Knowledge Base
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingKB ? "Edit" : "Create"} Knowledge Base</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  placeholder="E.g., Product Documentation, FAQ, Technical Guides"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  placeholder="Brief description of this knowledge base"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                />
              </div>
              <div>
                <Label>Associated AI Actors</Label>
                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                  {actors.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center">No actors available</p>
                  ) : (
                    actors.map(actor => (
                      <div key={actor.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.associated_actors.includes(actor.id)}
                          onChange={() => toggleActorAssociation(actor.id)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{actor.name}</span>
                      </div>
                    ))
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Select which AI Actors can access this knowledge base
                </p>
              </div>
              <Button onClick={editingKB ? handleUpdate : handleCreate} className="w-full">
                {editingKB ? "Update" : "Create"} Knowledge Base
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Knowledge Base List */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">Knowledge Bases ({knowledgeBases.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center text-muted-foreground py-4">Loading...</p>
            ) : knowledgeBases.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">No knowledge bases yet</p>
            ) : (
              <div className="space-y-2">
                {knowledgeBases.map(kb => (
                  <div
                    key={kb.id}
                    className={`border rounded-lg p-3 cursor-pointer transition ${
                      selectedKB?.id === kb.id ? "bg-blue-50 border-blue-200" : "hover:bg-slate-50"
                    }`}
                    onClick={() => fetchKBDetails(kb.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm">{kb.name}</h3>
                        {kb.description && (
                          <p className="text-xs text-muted-foreground mt-1">{kb.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            {kb.item_count} items
                          </Badge>
                          {kb.associated_actors && kb.associated_actors.length > 0 && (
                            <Badge variant="outline" className="text-xs">
                              <Users className="w-3 h-3 mr-1" />
                              {kb.associated_actors.length} actors
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditDialog(kb);
                          }}
                        >
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(kb.id);
                          }}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Knowledge Base Details with Tabs */}
        <Card className="lg:col-span-2">
          {!selectedKB ? (
            <CardContent className="py-12">
              <div className="text-center">
                <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Select a knowledge base to view details</p>
              </div>
            </CardContent>
          ) : (
            <>
              <CardHeader>
                <CardTitle className="text-base">{selectedKB.name}</CardTitle>
                {selectedKB.description && (
                  <CardDescription>{selectedKB.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="items">Items</TabsTrigger>
                    <TabsTrigger value="monitors">Monitors</TabsTrigger>
                    <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
                    <TabsTrigger value="analytics">Analytics</TabsTrigger>
                  </TabsList>

                  {/* Items Tab - (Existing functionality continues in next message due to length) */}
                  <TabsContent value="items" className="space-y-4">
                    {/* Action buttons */}
                    <div className="flex gap-2">
                      <Dialog open={manualItemDialogOpen} onOpenChange={setManualItemDialogOpen}>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm">
                            <Plus className="w-4 h-4 mr-2" />
                            Add Manual Entry
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Add Manual Entry</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <Label>Source Name (Optional)</Label>
                              <Input
                                placeholder="E.g., Pricing Policy 2025"
                                value={manualItem.source_name}
                                onChange={(e) => setManualItem({...manualItem, source_name: e.target.value})}
                              />
                            </div>
                            
                            {/* Tags Input */}
                            <div>
                              <Label>Tags (for AI function calling)</Label>
                              <div className="flex gap-2 mb-2">
                                <Input
                                  placeholder="e.g., pricing, services, company"
                                  value={currentTag}
                                  onChange={(e) => setCurrentTag(e.target.value)}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      if (currentTag.trim() && !manualItem.tags.includes(currentTag.trim())) {
                                        setManualItem({
                                          ...manualItem,
                                          tags: [...manualItem.tags, currentTag.trim().toLowerCase()]
                                        });
                                        setCurrentTag("");
                                      }
                                    }
                                  }}
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    if (currentTag.trim() && !manualItem.tags.includes(currentTag.trim())) {
                                      setManualItem({
                                        ...manualItem,
                                        tags: [...manualItem.tags, currentTag.trim().toLowerCase()]
                                      });
                                      setCurrentTag("");
                                    }
                                  }}
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                              </div>
                              
                              {/* Display tags */}
                              {manualItem.tags.length > 0 && (
                                <div className="flex flex-wrap gap-2 p-2 bg-slate-50 rounded border">
                                  {manualItem.tags.map((tag, index) => (
                                    <Badge key={index} variant="secondary" className="gap-1">
                                      {tag}
                                      <button
                                        onClick={() => {
                                          setManualItem({
                                            ...manualItem,
                                            tags: manualItem.tags.filter((_, i) => i !== index)
                                          });
                                        }}
                                        className="ml-1 hover:text-red-600"
                                      >
                                        ×
                                      </button>
                                    </Badge>
                                  ))}
                                </div>
                              )}
                              
                              <p className="text-xs text-muted-foreground mt-1">
                                Suggested: pricing, services, company, technical, contact
                              </p>
                            </div>
                            
                            <div>
                              <Label>Content</Label>
                              <Textarea
                                placeholder="Enter the knowledge content..."
                                rows={12}
                                value={manualItem.content}
                                onChange={(e) => setManualItem({...manualItem, content: e.target.value})}
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                Enter detailed information that will be used to answer questions
                              </p>
                            </div>
                            <Button onClick={handleAddManualItem} className="w-full">
                              Add to Knowledge Base
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>

                      <Dialog 
                        open={uploadDialogOpen} 
                        onOpenChange={(open) => {
                          // Prevent closing dialog during upload
                          if (!uploadingDocument || !open) {
                            setUploadDialogOpen(open);
                          }
                        }}
                      >
                        <DialogTrigger asChild>
                          <Button size="sm">
                            <Upload className="w-4 h-4 mr-2" />
                            Upload Document
                          </Button>
                        </DialogTrigger>
                        <DialogContent 
                          onInteractOutside={(e) => {
                            // Prevent closing by clicking outside during upload
                            if (uploadingDocument) {
                              e.preventDefault();
                            }
                          }}
                          onEscapeKeyDown={(e) => {
                            // Prevent closing with Escape during upload
                            if (uploadingDocument) {
                              e.preventDefault();
                            }
                          }}
                        >
                          <DialogHeader>
                            <DialogTitle>Upload Document</DialogTitle>
                            {uploadingDocument && (
                              <p className="text-xs text-amber-600 mt-1">⚠️ Please wait while processing...</p>
                            )}
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <Label>Select File</Label>
                              <div className="flex gap-2">
                                <Input
                                  id="file-upload"
                                  type="file"
                                  accept=".txt,.md,.csv,.pdf,.docx"
                                  onChange={(e) => setUploadFile(e.target.files[0])}
                                  className="hidden"
                                  disabled={uploadingDocument}
                                />
                                <label
                                  htmlFor="file-upload"
                                  className={`flex-1 inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 ${
                                    uploadingDocument ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'cursor-pointer'
                                  }`}
                                >
                                  <Upload className="w-4 h-4 mr-2" />
                                  Choose File
                                </label>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                Supported: .txt, .md, .csv, .pdf, .docx
                              </p>
                            </div>
                            {uploadFile && (
                              <div className="p-3 bg-slate-50 rounded text-sm border border-slate-200">
                                <p className="text-xs font-semibold text-slate-600 mb-1">Selected File:</p>
                                <p className="text-sm font-medium break-all">{uploadFile.name}</p>
                                <p className="text-xs text-slate-600 mt-1"><strong>Size:</strong> {(uploadFile.size / 1024).toFixed(2)} KB</p>
                              </div>
                            )}
                            
                            {/* AI Correction Model Selector */}
                            <div>
                              <Label>AI Correction Model (for PDFs)</Label>
                              <Select value={correctionModel} onValueChange={setCorrectionModel} disabled={uploadingDocument}>
                                <SelectTrigger disabled={uploadingDocument}>
                                  <SelectValue placeholder="Select AI model for text correction" />
                                </SelectTrigger>
                                <SelectContent className="max-h-[400px] overflow-y-auto">
                                  {loadingModels ? (
                                    <SelectItem value="loading" disabled>Loading models...</SelectItem>
                                  ) : openrouterModels.length > 0 ? (
                                    <>
                                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">Recommended Free Models</div>
                                      <SelectItem value="google/gemini-2.0-flash-exp:free">Google Gemini 2.0 Flash (Free) ⚡</SelectItem>
                                      <SelectItem value="deepseek/deepseek-r1:free">DeepSeek R1 (Free)</SelectItem>
                                      <SelectItem value="qwen/qwen-2.5-72b-instruct:free">Qwen 2.5 72B (Free)</SelectItem>
                                      
                                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mt-2">Premium Models</div>
                                      <SelectItem value="openai/gpt-4o">OpenAI GPT-4o (Best Quality)</SelectItem>
                                      <SelectItem value="openai/gpt-4o-mini">OpenAI GPT-4o-mini (Fast)</SelectItem>
                                      <SelectItem value="anthropic/claude-3.5-sonnet">Claude 3.5 Sonnet</SelectItem>
                                      
                                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mt-2">All Free Models</div>
                                      {openrouterModels.filter(m => m.id.includes(':free')).slice(0, 15).map((model) => (
                                        <SelectItem key={model.id} value={model.id}>
                                          {model.name || model.id}
                                        </SelectItem>
                                      ))}
                                    </>
                                  ) : (
                                    <SelectItem value="none" disabled>No models available</SelectItem>
                                  )}
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-muted-foreground mt-1">
                                AI will automatically fix spacing and formatting issues in parsed PDFs
                              </p>
                            </div>
                            
                            {/* Progress Bar */}
                            {uploadingDocument && (
                              <div className="space-y-2">
                                <div className="flex items-center justify-between text-sm">
                                  <span className="font-medium text-blue-600">{uploadStatus}</span>
                                  <span className="text-sm text-muted-foreground">{uploadProgress}%</span>
                                </div>
                                <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                                  <div 
                                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                                    style={{ width: `${uploadProgress}%` }}
                                  ></div>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  {uploadProgress < 40 && "⏳ Uploading file..."}
                                  {uploadProgress >= 40 && uploadProgress < 80 && "🤖 AI is analyzing and correcting text..."}
                                  {uploadProgress >= 80 && uploadProgress < 100 && "📦 Creating chunks..."}
                                  {uploadProgress === 100 && "✅ Done!"}
                                </p>
                              </div>
                            )}
                            
                            <Button 
                              onClick={handleUploadDocument} 
                              disabled={!uploadFile || uploadingDocument} 
                              className="w-full"
                            >
                              {uploadingDocument ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                  Processing...
                                </>
                              ) : (
                                "Upload and Parse with AI Correction"
                              )}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>

                      <Dialog open={websiteDialogOpen} onOpenChange={setWebsiteDialogOpen}>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="outline">
                            <LinkIcon className="w-4 h-4 mr-2" />
                            Add Website
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Add Website URL</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <Label>Website URL</Label>
                              <Input
                                type="url"
                                placeholder="https://example.com"
                                value={websiteUrl}
                                onChange={(e) => setWebsiteUrl(e.target.value)}
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                The website will be scraped and its content added to the knowledge base
                              </p>
                            </div>
                            <Button onClick={handleAddWebsite} disabled={!websiteUrl} className="w-full">
                              Scrape and Add
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>

                    {/* Associated Actors */}
                    {selectedKB.associated_actors && selectedKB.associated_actors.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold mb-2">Associated Actors:</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedKB.associated_actors.map(actorId => {
                            const actor = actors.find(a => a.id === actorId);
                            return actor ? (
                              <Badge key={actorId} variant="outline">
                                {actor.name}
                              </Badge>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}

                    {/* Items List */}
                    <div>
                      <h4 className="text-sm font-semibold mb-2">Items ({selectedKB.items?.length || 0}):</h4>
                      {!selectedKB.items || selectedKB.items.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-8">
                          No items yet. Upload a document or add a manual entry.
                        </p>
                      ) : (
                        <>
                          <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-sm font-medium text-blue-900">
                              📚 {selectedKB.items.length} knowledge items
                            </p>
                            <p className="text-xs text-blue-700 mt-1">
                              Documents are chunked into smaller pieces for better AI retrieval. Multiple items from one document is normal.
                            </p>
                          </div>
                          
                          {/* Sync Progress Banner */}
                          {(() => {
                            const activeSyncs = Object.entries(syncProgress).filter(([_, prog]) => 
                              prog.status === 'syncing' || prog.status === 'paused'
                            );
                            
                            return activeSyncs.length > 0 && (
                              <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-4 shadow-lg">
                                {activeSyncs.map(([webhookId, progress]) => (
                                  <div key={webhookId}>
                                    <div className="flex items-center justify-between mb-2">
                                      <div>
                                        <p className="text-sm font-bold text-purple-900">
                                          {progress.status === 'syncing' ? '🔄 Sync in Progress' : '⏸️ Sync Paused'}
                                        </p>
                                        <p className="text-xs text-purple-700">
                                          {progress.clients_processed?.toLocaleString()} / {progress.total_in_response?.toLocaleString()} clients | ETA: {progress.eta}
                                        </p>
                                      </div>
                                      <div className="text-right">
                                        <p className="text-3xl font-bold text-purple-600">{progress.progress_percentage?.toFixed(1)}%</p>
                                      </div>
                                    </div>
                                    
                                    {/* Progress Bar */}
                                    <div className="w-full bg-purple-200 rounded-full h-3 mb-2">
                                      <div 
                                        className="bg-purple-600 h-3 rounded-full transition-all duration-300"
                                        style={{width: `${progress.progress_percentage || 0}%`}}
                                      />
                                    </div>
                                    
                                    <div className="flex justify-between text-xs text-purple-700">
                                      <span>⏱️ Elapsed: {progress.elapsed_time}</span>
                                      <span>📝 {progress.items_added?.toLocaleString()} items created</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                          
                          {/* Statistics Banner */}
                          {(() => {
                            const webhookItems = selectedKB.items.filter(item => item.source_type === 'webhook');
                            const uniqueClients = new Set(webhookItems.map(item => item.metadata?.client_name).filter(Boolean));
                            const hasWebhookItems = webhookItems.length > 0;
                            const noActiveSyncs = Object.values(syncProgress).every(p => p.status === 'completed' || !p.status);
                            
                            return hasWebhookItems && noActiveSyncs && (
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-4">
                                    <div>
                                      <p className="text-sm font-semibold text-blue-900">Webhook Data Statistics</p>
                                      <p className="text-xs text-blue-700">Real-time client and entity information</p>
                                    </div>
                                  </div>
                                  <div className="flex gap-6">
                                    <div className="text-center">
                                      <p className="text-2xl font-bold text-blue-600">{uniqueClients.size}</p>
                                      <p className="text-xs text-blue-700">Unique Clients</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-2xl font-bold text-blue-600">{webhookItems.length}</p>
                                      <p className="text-xs text-blue-700">Total Entities</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                          
                          {/* Search Bar */}
                          <div className="relative">
                            <Input
                              placeholder="🔍 Search by client name, ID, or any content..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="w-full pl-10"
                            />
                            {searchQuery && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="absolute right-2 top-1/2 -translate-y-1/2 h-6 px-2"
                                onClick={() => setSearchQuery("")}
                              >
                                ✕
                              </Button>
                            )}
                          </div>
                          
                          {/* Search Results Indicator */}
                          {searchQuery && (
                            <div className="text-sm text-muted-foreground">
                              {(() => {
                                const filteredItems = selectedKB.items.filter(item => {
                                  const query = searchQuery.toLowerCase();
                                  return item.content.toLowerCase().includes(query) ||
                                         item.metadata?.client_name?.toLowerCase().includes(query) ||
                                         item.source_name?.toLowerCase().includes(query) ||
                                         item.tags?.some(tag => tag.toLowerCase().includes(query)) ||
                                         item.metadata?.entity_type?.toLowerCase().includes(query);
                                });
                                return `🔍 Found ${filteredItems.length} items matching "${searchQuery}"`;
                              })()}
                            </div>
                          )}
                          
                          <div className="space-y-4 max-h-96 overflow-y-auto">
                            {/* Group items by client (for webhooks) or source_name (for others) */}
                            {Object.entries(
                              selectedKB.items
                                .filter(item => {
                                  if (!searchQuery) return true;
                                  const query = searchQuery.toLowerCase();
                                  
                                  // Search in content
                                  if (item.content.toLowerCase().includes(query)) return true;
                                  
                                  // Search in client name (for webhooks)
                                  if (item.metadata?.client_name?.toLowerCase().includes(query)) return true;
                                  
                                  // Search in source name
                                  if (item.source_name?.toLowerCase().includes(query)) return true;
                                  
                                  // Search in tags
                                  if (item.tags?.some(tag => tag.toLowerCase().includes(query))) return true;
                                  
                                  // Search in entity type (for webhooks)
                                  if (item.metadata?.entity_type?.toLowerCase().includes(query)) return true;
                                  
                                  return false;
                                })
                                .reduce((acc, item) => {
                                  // For webhook items, group by client name
                                  let key;
                                  if (item.source_type === 'webhook' && item.metadata?.client_name) {
                                    key = item.metadata.client_name;
                                  } else {
                                    key = item.source_name || "Untitled";
                                  }
                                  if (!acc[key]) acc[key] = [];
                                  acc[key].push(item);
                                  return acc;
                                }, {})
                            ).map(([groupName, items]) => {
                              const isWebhook = items[0].source_type === 'webhook';
                              const originalSourceName = items[0].source_name;
                              
                              return (
                              <div key={groupName} className="border rounded-lg p-3 bg-slate-50">
                                <div className="flex items-start justify-between gap-2 mb-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                      <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                                      <span className="text-sm font-semibold truncate" title={groupName}>
                                        {groupName}
                                      </span>
                                      <Badge variant="outline" className="text-xs flex-shrink-0">
                                        {items[0].source_type}
                                      </Badge>
                                      {isWebhook && (
                                        <Badge variant="secondary" className="text-xs flex-shrink-0">
                                          {items.length} entities
                                        </Badge>
                                      )}
                                      {!isWebhook && (
                                        <Badge variant="secondary" className="text-xs flex-shrink-0">
                                          {items.length} chunks
                                        </Badge>
                                      )}
                                    </div>
                                    {isWebhook && (
                                      <p className="text-xs text-muted-foreground">Source: {originalSourceName}</p>
                                    )}
                                  </div>
                                  <div className="flex gap-1 flex-shrink-0">
                                    {!isWebhook && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleViewFullDocument(originalSourceName)}
                                        className="text-blue-600 hover:text-blue-700"
                                      >
                                        <Eye className="w-3 h-3 mr-1" />
                                        View Full
                                      </Button>
                                    )}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        if (window.confirm(`Delete all ${items.length} items of "${groupName}"?`)) {
                                          items.forEach(item => handleDeleteItem(item.id));
                                        }
                                      }}
                                      className="text-red-600 hover:text-red-700"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                                
                                {/* For webhooks, show sub-items list. For documents, show preview */}
                                {isWebhook ? (
                                  <div className="space-y-2">
                                    {items.map((item, idx) => (
                                      <div key={item.id} className="bg-white p-2 rounded border border-slate-200">
                                        <div className="flex items-center justify-between mb-1">
                                          <span className="text-xs font-medium text-slate-900">
                                            {item.metadata?.entity_type || 'Unknown Entity'}
                                          </span>
                                          <div className="flex flex-wrap gap-1">
                                            {item.tags?.slice(0, 3).map(tag => (
                                              <Badge key={tag} variant="outline" className="text-xs">
                                                {tag}
                                              </Badge>
                                            ))}
                                          </div>
                                        </div>
                                        <p className="text-xs text-slate-700 whitespace-pre-wrap">
                                          {item.content.substring(0, 150)}
                                          {item.content.length > 150 && "..."}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="bg-white p-2 rounded border border-slate-200 mb-1">
                                    <p className="text-xs text-slate-700 whitespace-pre-wrap">
                                      {items[0].content.substring(0, 200)}
                                      {items[0].content.length > 200 && "..."}
                                    </p>
                                  </div>
                                )}
                                
                                {/* Tags Display & Edit */}
                                <div className="mt-2">
                                  {editingTags?.itemId === items[0].id ? (
                                    <div className="space-y-2">
                                      <div className="flex gap-2">
                                        <Input
                                          size="sm"
                                          placeholder="Add tag..."
                                          value={editTagInput}
                                          onChange={(e) => setEditTagInput(e.target.value)}
                                          onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                              e.preventDefault();
                                              const tag = editTagInput.trim().toLowerCase();
                                              if (tag && !editingTags.tags.includes(tag)) {
                                                setEditingTags({
                                                  ...editingTags,
                                                  tags: [...editingTags.tags, tag]
                                                });
                                                setEditTagInput("");
                                              }
                                            }
                                          }}
                                          className="flex-1"
                                        />
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            const tag = editTagInput.trim().toLowerCase();
                                            if (tag && !editingTags.tags.includes(tag)) {
                                              setEditingTags({
                                                ...editingTags,
                                                tags: [...editingTags.tags, tag]
                                              });
                                              setEditTagInput("");
                                            }
                                          }}
                                        >
                                          Add
                                        </Button>
                                      </div>
                                      
                                      {editingTags.tags && editingTags.tags.length > 0 && (
                                        <div className="flex flex-wrap gap-1">
                                          {editingTags.tags.map((tag, idx) => (
                                            <Badge key={idx} variant="secondary" className="text-xs">
                                              {tag}
                                              <button
                                                onClick={() => {
                                                  setEditingTags({
                                                    ...editingTags,
                                                    tags: editingTags.tags.filter((_, i) => i !== idx)
                                                  });
                                                }}
                                                className="ml-1 hover:text-red-600"
                                              >
                                                ×
                                              </button>
                                            </Badge>
                                          ))}
                                        </div>
                                      )}
                                      
                                      <div className="flex gap-2">
                                        <Button
                                          size="sm"
                                          onClick={() => handleUpdateItemTags(items[0].id, editingTags.tags || [])}
                                        >
                                          <Save className="w-3 h-3 mr-1" />
                                          Save Tags
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            setEditingTags(null);
                                            setEditTagInput("");
                                          }}
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-muted-foreground">Tags:</span>
                                      {items[0].tags && items[0].tags.length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                          {items[0].tags.map((tag, idx) => (
                                            <Badge key={idx} variant="secondary" className="text-xs">
                                              {tag}
                                            </Badge>
                                          ))}
                                        </div>
                                      ) : (
                                        <span className="text-xs text-slate-400 italic">No tags</span>
                                      )}
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => {
                                          console.log("Edit clicked for item:", items[0].id, "Current tags:", items[0].tags);
                                          setEditingTags({ itemId: items[0].id, tags: items[0].tags || [] });
                                        }}
                                        className="text-xs h-6"
                                      >
                                        <Edit2 className="w-3 h-3 mr-1" />
                                        Edit
                                      </Button>
                                    </div>
                                  )}
                                </div>
                                
                                <p className="text-xs text-muted-foreground mt-1">
                                  Added on {new Date(items[0].created_at).toLocaleDateString()} • 
                                  {isWebhook ? 
                                    `${items.length} entities` : 
                                    `Total: ${items.reduce((sum, i) => sum + i.content.length, 0)} characters across ${items.length} chunks`
                                  }
                                </p>
                              </div>
                            );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  </TabsContent>

                  {/* Monitors Tab */}
                  <TabsContent value="monitors" className="space-y-4">
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-muted-foreground">Configure websites to monitor for automatic updates</p>
                      <Dialog open={monitorDialogOpen} onOpenChange={setMonitorDialogOpen}>
                        <DialogTrigger asChild>
                          <Button size="sm">
                            <Plus className="w-4 h-4 mr-2" />
                            Add Monitor
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Configure Website Monitoring</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <Label>Website URL</Label>
                              <Input
                                type="url"
                                placeholder="https://example.com"
                                value={monitorConfig.url}
                                onChange={(e) => setMonitorConfig({...monitorConfig, url: e.target.value})}
                              />
                            </div>
                            <div>
                              <Label>Update Frequency (hours)</Label>
                              <Input
                                type="number"
                                min="1"
                                value={monitorConfig.update_frequency}
                                onChange={(e) => setMonitorConfig({...monitorConfig, update_frequency: parseInt(e.target.value)})}
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                How often to check for updates
                              </p>
                            </div>
                            <Button onClick={handleAddMonitor} className="w-full">
                              Configure Monitoring
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>

                    {monitors.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">No monitors configured</p>
                    ) : (
                      <div className="space-y-3">
                        {monitors.map(monitor => (
                          <div key={monitor.id} className="border rounded-lg p-4 bg-slate-50">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <Clock className="w-4 h-4 text-blue-600" />
                                  <span className="font-semibold text-sm">{monitor.url}</span>
                                  <Badge variant={monitor.status === "active" ? "default" : "destructive"}>
                                    {monitor.status}
                                  </Badge>
                                </div>
                                <div className="text-xs text-muted-foreground space-y-1">
                                  <p>Update every {monitor.update_frequency} hours</p>
                                  <p>Last scraped: {monitor.last_scraped ? new Date(monitor.last_scraped).toLocaleString() : "Never"}</p>
                                  <p>Next scrape: {monitor.next_scrape ? new Date(monitor.next_scrape).toLocaleString() : "N/A"}</p>
                                  <p>Items: {monitor.item_count}</p>
                                  {monitor.error_message && (
                                    <p className="text-red-600">Error: {monitor.error_message}</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleTriggerMonitorUpdate(monitor.id)}
                                  title="Trigger update now"
                                >
                                  <RefreshCw className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteMonitor(monitor.id)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  {/* Webhooks Tab */}
                  <TabsContent value="webhooks" className="space-y-4">
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-muted-foreground">Configure webhooks for private API integration</p>
                      <Dialog open={webhookDialogOpen} onOpenChange={(open) => {
                        setWebhookDialogOpen(open);
                        if (!open) {
                          setWebhookStep(1);
                          setApiAnalysis(null);
                        }
                      }}>
                        <DialogTrigger asChild>
                          <Button size="sm">
                            <Plus className="w-4 h-4 mr-2" />
                            Add Webhook
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>Configure Webhook - Step {webhookStep} of 2</DialogTitle>
                          </DialogHeader>
                          
                          {webhookStep === 1 && (
                            <div className="space-y-4">
                              <div>
                                <Label>Name</Label>
                                <Input
                                  placeholder="E.g., Client Profiles API"
                                  value={webhookConfig.name}
                                  onChange={(e) => setWebhookConfig({...webhookConfig, name: e.target.value})}
                                />
                              </div>
                              <div>
                                <Label>Webhook URL</Label>
                                <Input
                                  type="url"
                                  placeholder="https://api.example.com/data"
                                  value={webhookConfig.webhook_url}
                                  onChange={(e) => setWebhookConfig({...webhookConfig, webhook_url: e.target.value})}
                                />
                              </div>
                              <div>
                                <Label>Authentication Type</Label>
                                <Select value={webhookConfig.auth_type} onValueChange={(value) => setWebhookConfig({...webhookConfig, auth_type: value})}>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">None</SelectItem>
                                    <SelectItem value="bearer">Bearer Token</SelectItem>
                                    <SelectItem value="api_key">API Key</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              {webhookConfig.auth_type !== "none" && (
                                <div>
                                  <Label>Auth Token</Label>
                                  <Input
                                    type="password"
                                    placeholder="Enter token"
                                    value={webhookConfig.auth_token}
                                    onChange={(e) => setWebhookConfig({...webhookConfig, auth_token: e.target.value})}
                                  />
                                </div>
                              )}
                              <div>
                                <Label>Sync Frequency (hours)</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  value={webhookConfig.sync_frequency}
                                  onChange={(e) => setWebhookConfig({...webhookConfig, sync_frequency: parseInt(e.target.value)})}
                                />
                              </div>
                              <Button onClick={handleAnalyzeAPI} className="w-full" disabled={analyzingAPI || !webhookConfig.webhook_url}>
                                {analyzingAPI ? "Analyzing..." : "Analyze API & Configure Entities"}
                              </Button>
                            </div>
                          )}

                          {webhookStep === 2 && (
                            <div className="space-y-4">
                              {apiAnalysis && (
                                <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm">
                                  <p><strong>Total Items:</strong> {apiAnalysis.total_items}</p>
                                  <p><strong>Sample Client:</strong> {apiAnalysis.sample_client_name}</p>
                                  <p><strong>Detected Entities:</strong> {apiAnalysis.detected_entities.length}</p>
                                </div>
                              )}
                              
                              <div>
                                <Label>Primary Key (Client Identifier)</Label>
                                <Select value={webhookConfig.primary_key} onValueChange={(value) => setWebhookConfig({...webhookConfig, primary_key: value})}>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {apiAnalysis?.available_keys.map(key => (
                                      <SelectItem key={key} value={key}>{key}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground mt-1">
                                  Field used to group data by client
                                </p>
                              </div>

                              <div>
                                <Label>Entity Extraction Rules</Label>
                                <div className="border rounded p-3 space-y-2 max-h-60 overflow-y-auto">
                                  {webhookConfig.entity_rules.map((rule, index) => (
                                    <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                                      <input
                                        type="checkbox"
                                        checked={rule.enabled}
                                        onChange={(e) => {
                                          const newRules = [...webhookConfig.entity_rules];
                                          newRules[index].enabled = e.target.checked;
                                          setWebhookConfig({...webhookConfig, entity_rules: newRules});
                                        }}
                                        className="w-4 h-4"
                                      />
                                      <div className="flex-1 text-sm">
                                        <p className="font-medium">{rule.name}</p>
                                        <p className="text-xs text-muted-foreground">Path: {rule.path}</p>
                                        <div className="flex gap-1 mt-1">
                                          {rule.tags.map(tag => (
                                            <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                                          ))}
                                        </div>
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          const newRules = webhookConfig.entity_rules.filter((_, i) => i !== index);
                                          setWebhookConfig({...webhookConfig, entity_rules: newRules});
                                        }}
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              <div>
                                <Label>Custom Tags (Applied to All Items)</Label>
                                <div className="flex gap-2">
                                  <Input
                                    placeholder="e.g., legal, immigration"
                                    value={customTagInput}
                                    onChange={(e) => setCustomTagInput(e.target.value)}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter' && customTagInput.trim()) {
                                        setWebhookConfig({
                                          ...webhookConfig,
                                          custom_tags: [...webhookConfig.custom_tags, customTagInput.trim()]
                                        });
                                        setCustomTagInput("");
                                      }
                                    }}
                                  />
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      if (customTagInput.trim()) {
                                        setWebhookConfig({
                                          ...webhookConfig,
                                          custom_tags: [...webhookConfig.custom_tags, customTagInput.trim()]
                                        });
                                        setCustomTagInput("");
                                      }
                                    }}
                                  >
                                    Add
                                  </Button>
                                </div>
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {webhookConfig.custom_tags.map((tag, index) => (
                                    <Badge key={index} variant="outline">
                                      {tag}
                                      <button
                                        onClick={() => {
                                          const newTags = webhookConfig.custom_tags.filter((_, i) => i !== index);
                                          setWebhookConfig({...webhookConfig, custom_tags: newTags});
                                        }}
                                        className="ml-1 text-red-600"
                                      >
                                        ×
                                      </button>
                                    </Badge>
                                  ))}
                                </div>
                              </div>

                              <div className="flex gap-2">
                                <Button variant="outline" onClick={() => setWebhookStep(1)} className="flex-1">
                                  Back
                                </Button>
                                <Button onClick={handleAddWebhook} className="flex-1">
                                  Create Webhook
                                </Button>
                              </div>
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>
                    </div>

                    {webhooks.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">No webhooks configured</p>
                    ) : (
                      <div className="space-y-3">
                        {webhooks.map(webhook => (
                          <div key={webhook.id} className="border rounded-lg p-4 bg-slate-50">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <Webhook className="w-4 h-4 text-purple-600" />
                                  <span className="font-semibold text-sm">{webhook.name}</span>
                                  <Badge variant={webhook.status === "active" ? "default" : "destructive"}>
                                    {webhook.status}
                                  </Badge>
                                </div>
                                <div className="text-xs text-muted-foreground space-y-1">
                                  <p>URL: {webhook.webhook_url}</p>
                                  <p>Auth: {webhook.auth_type}</p>
                                  <p>Sync every {webhook.sync_frequency} hours</p>
                                  <p>Last synced: {webhook.last_synced ? new Date(webhook.last_synced).toLocaleString() : "Never"}</p>
                                  <p>Next sync: {webhook.next_sync ? new Date(webhook.next_sync).toLocaleString() : "N/A"}</p>
                                  
                                  {/* Sync Progress */}
                                  {syncProgress[webhook.id] && syncProgress[webhook.id].status !== 'completed' && (
                                    <div className="mt-2 p-3 bg-purple-50 border border-purple-200 rounded space-y-2">
                                      <div className="flex items-center justify-between">
                                        <p className="font-semibold text-purple-900">
                                          {syncProgress[webhook.id].status === 'syncing' ? '🔄 Syncing...' : '⏸️ Paused'}
                                        </p>
                                        <p className="text-purple-700 font-bold">
                                          {syncProgress[webhook.id].progress_percentage?.toFixed(1)}%
                                        </p>
                                      </div>
                                      
                                      {/* Progress Bar */}
                                      <div className="w-full bg-purple-200 rounded-full h-2">
                                        <div 
                                          className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                                          style={{width: `${syncProgress[webhook.id].progress_percentage || 0}%`}}
                                        />
                                      </div>
                                      
                                      {/* Stats */}
                                      <div className="grid grid-cols-2 gap-2 text-xs">
                                        <div>
                                          <p className="text-purple-600">⏱️ Elapsed: {syncProgress[webhook.id].elapsed_time}</p>
                                          <p className="text-purple-600">🎯 ETA: {syncProgress[webhook.id].eta}</p>
                                        </div>
                                        <div>
                                          <p className="text-purple-600">📊 Batch: {syncProgress[webhook.id].current_batch}/{syncProgress[webhook.id].total_batches}</p>
                                          <p className="text-purple-600">👥 Clients: {syncProgress[webhook.id].clients_processed?.toLocaleString()}</p>
                                        </div>
                                      </div>
                                      <p className="text-purple-600">📝 Items: {syncProgress[webhook.id].items_added?.toLocaleString()} created</p>
                                      
                                      {/* Control Buttons */}
                                      <div className="flex gap-2 pt-2">
                                        {syncProgress[webhook.id].status === 'syncing' ? (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handlePauseSync(webhook.id)}
                                            className="flex-1"
                                          >
                                            ⏸️ Pause
                                          </Button>
                                        ) : (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleResumeSync(webhook.id)}
                                            className="flex-1"
                                          >
                                            ▶️ Resume
                                          </Button>
                                        )}
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => handleCancelSync(webhook.id)}
                                          className="flex-1 text-red-600 hover:text-red-700"
                                        >
                                          ❌ Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {webhookStats[webhook.id] && !syncProgress[webhook.id] && (
                                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                                      <p className="font-semibold text-blue-900">📊 Statistics:</p>
                                      <p className="text-blue-700">✅ Unique Clients: {webhookStats[webhook.id].unique_clients}</p>
                                      <p className="text-blue-700">📝 Total Items: {webhookStats[webhook.id].total_items}</p>
                                    </div>
                                  )}
                                  {webhook.error_message && (
                                    <p className="text-red-600">Error: {webhook.error_message}</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-row gap-2 flex-shrink-0 ml-4">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => fetchWebhookStats(webhook.id)}
                                  title="View client statistics"
                                  disabled={loadingStats[webhook.id]}
                                  className="hover:bg-blue-50"
                                >
                                  <BarChart3 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleTriggerWebhookSync(webhook.id)}
                                  title="Trigger sync now"
                                  className="hover:bg-green-50"
                                >
                                  <RefreshCw className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeleteWebhook(webhook.id)}
                                  className="text-red-600 hover:bg-red-50"
                                  title="Delete webhook"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  {/* Analytics Tab */}
                  <TabsContent value="analytics" className="space-y-4">
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-muted-foreground">View knowledge base usage analytics</p>
                      <Select value={analyticsDays.toString()} onValueChange={(value) => setAnalyticsDays(parseInt(value))}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="7">Last 7 days</SelectItem>
                          <SelectItem value="30">Last 30 days</SelectItem>
                          <SelectItem value="90">Last 90 days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {!analytics ? (
                      <p className="text-center text-muted-foreground py-8">Loading analytics...</p>
                    ) : (
                      <div className="space-y-4">
                        {/* Stats Cards */}
                        <div className="grid grid-cols-3 gap-4">
                          <Card>
                            <CardContent className="pt-6">
                              <div className="text-2xl font-bold">{analytics.total_uses}</div>
                              <p className="text-xs text-muted-foreground">Total Uses</p>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent className="pt-6">
                              <div className="text-2xl font-bold">{analytics.unique_queries}</div>
                              <p className="text-xs text-muted-foreground">Unique Queries</p>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardContent className="pt-6">
                              <div className="text-2xl font-bold">{analytics.unique_sessions}</div>
                              <p className="text-xs text-muted-foreground">Unique Sessions</p>
                            </CardContent>
                          </Card>
                        </div>

                        {/* Top Items */}
                        <div>
                          <h4 className="text-sm font-semibold mb-3">Most Queried Items:</h4>
                          {analytics.top_items && analytics.top_items.length > 0 ? (
                            <div className="space-y-2">
                              {analytics.top_items.map((item, index) => (
                                <div key={item.item_id} className="border rounded-lg p-3 bg-slate-50">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-1">
                                        <Badge variant="outline">{index + 1}</Badge>
                                        <span className="text-sm font-semibold">{item.source_name}</span>
                                        <Badge variant="outline" className="text-xs">{item.source_type}</Badge>
                                      </div>
                                      <p className="text-xs text-slate-700">{item.content_preview}</p>
                                      <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                                        <span>Uses: {item.usage_count}</span>
                                        <span>Avg Similarity: {item.avg_similarity}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-center text-muted-foreground py-4">No usage data yet</p>
                          )}
                        </div>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </>
          )}
        </Card>
      </div>
      
      {/* Full Document Content Viewer/Editor Dialog */}
      <Dialog open={fullContentDialogOpen} onOpenChange={setFullContentDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {editingContent ? "Edit" : "View"} Full Document: {selectedDocument}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {loadingFullContent ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : editingContent ? (
              <Textarea
                value={fullDocumentContent}
                onChange={(e) => setFullDocumentContent(e.target.value)}
                rows={25}
                className="font-mono text-sm"
                placeholder="Edit document content..."
              />
            ) : (
              <div className="bg-slate-50 p-4 rounded border border-slate-200">
                <pre className="text-sm whitespace-pre-wrap font-mono">{fullDocumentContent}</pre>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="text-xs text-muted-foreground">
              {fullDocumentContent.length} characters
            </div>
            <div className="flex gap-2">
              {editingContent ? (
                <>
                  <Button variant="outline" onClick={() => setEditingContent(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleUpdateFullDocument}>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setFullContentDialogOpen(false)}>
                    Close
                  </Button>
                  <Button onClick={() => setEditingContent(true)}>
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit Content
                  </Button>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
